// File: frontend/src/components/admin/tabs/InsightsTab.js
//
// GET /api/admin/insights. Conclusions rather than counts: do people who sign
// up start, do they come back, and how many are only ever counted once.
//
// Every figure here states what it measures. The metric this replaces was
// labelled "Retention Rate" while actually computing weekly-active ÷ all users,
// which drifts downward during growth and had presumably been read as decline.
import { TrendingUp, UserPlus, Repeat, Activity, CreditCard, UserX, Smartphone } from 'lucide-react';
import { Card, StatCard, StatGrid, SectionError, EmptyState, Loading } from '../primitives';
import { RankedBars, CHART_TOKENS } from '../charts';

const pctText = (v) => (v === null || v === undefined ? '—' : `${v}%`);

// A retention curve reads better as a row of marks than as a table of cohorts.
function RetentionSummary({ summary, marks }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {marks.map(mark => {
        const row = summary[`d${mark}`];
        return (
          <div key={mark} className="bg-surface-input border border-divider rounded-xl p-3 text-center">
            <div className="text-xs text-ink-subtle uppercase tracking-wide">Day {mark}</div>
            <div className="admin-nums text-xl sm:text-2xl font-bold text-ink-main mt-1">{pctText(row?.rate)}</div>
            <div className="text-xs text-ink-light mt-1">
              {row?.retained ?? 0} of {row?.size ?? 0}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Per-cohort rows, newest first. Cohorts too young to have reached a mark show
// a dash rather than 0% — the distinction matters, because a zero there reads
// as total churn when it actually means "ask again later".
function CohortTable({ rows, marks }) {
  const recent = [...rows].reverse().filter(r => r.size > 0).slice(0, 14);
  if (recent.length === 0) return <EmptyState>No signups in this window</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-ink-light border-b border-divider">
            <th className="py-2 pr-2 font-medium">Signed up</th>
            <th className="py-2 px-2 font-medium text-right">Size</th>
            {marks.map(m => <th key={m} className="py-2 px-2 font-medium text-right whitespace-nowrap">Day {m}</th>)}
          </tr>
        </thead>
        <tbody>
          {recent.map(row => (
            <tr key={row.cohort} className="border-b border-divider last:border-0">
              <td className="py-2 pr-2 text-ink-main whitespace-nowrap">{row.cohort}</td>
              <td className="py-2 px-2 text-right admin-nums text-ink-light">{row.size}</td>
              {marks.map(m => (
                <td key={m} className="py-2 px-2 text-right admin-nums text-ink-light whitespace-nowrap">
                  {row[`d${m}Rate`] === null
                    ? <span className="text-ink-subtle" title={`This cohort is only ${row.age} days old`}>—</span>
                    : `${row[`d${m}Rate`]}%`}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InsightsTab({ data, error, loading, onRetry }) {
  if (error) return <SectionError message={error} onRetry={onRetry} />;
  if (loading && !data) return <Loading>Working out cohorts…</Loading>;
  if (!data) return null;

  const { cohorts, activation, activeUsers, neverActivated, subscriptions } = data;

  return (
    <div className="space-y-6">
      <StatGrid cols={4}>
        <StatCard
          title="Activation"
          value={pctText(activation.rate)}
          subtitle={`${activation.activated} of ${activation.eligible} signups`}
          icon={UserPlus}
          tone="success"
          hint={`Logged at least one habit within ${activation.windowDays} days of joining.`}
        />
        <StatCard
          title="Day 7 retention"
          value={pctText(cohorts.summary.d7?.rate)}
          subtitle={`${cohorts.summary.d7?.cohorts ?? 0} cohorts old enough to count`}
          icon={Repeat}
          tone="info"
          hint="Share of a signup cohort still active a week or more later."
        />
        <StatCard
          title="Stickiness"
          value={pctText(activeUsers.stickiness.logged)}
          subtitle="Daily ÷ monthly active"
          icon={Activity}
          tone="accent"
          hint="Of everyone who logged a habit this month, the share who logged one today."
        />
        <StatCard
          title="Pro"
          value={pctText(subscriptions.share)}
          subtitle={`${subscriptions.pro} of ${subscriptions.total} accounts`}
          icon={CreditCard}
          tone="warning"
        />
      </StatGrid>

      <Card title={`Retention by signup cohort (${cohorts.windowDays} days)`} icon={TrendingUp} tone="info">
        <RetentionSummary summary={cohorts.summary} marks={cohorts.marks} />
        <p className="text-xs text-ink-subtle mt-3 leading-snug">
          &ldquo;Retained at day N&rdquo; means active on day N <em>or later</em>, not on exactly day N —
          someone who tracks Monday, Wednesday and Friday has not churned because day 7 fell on a
          Tuesday. Each mark is pooled over only those cohorts old enough to have reached it, so the
          three have <em>different denominators</em> (shown beneath each) and a later mark can read
          higher than an earlier one. Within any single cohort the curve only ever falls.
        </p>
        <div className="mt-4">
          <CohortTable rows={cohorts.rows} marks={cohorts.marks} />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card title="Two kinds of active" icon={Smartphone} tone="info">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-ink-main mb-2">Logged a habit</h4>
              <RankedBars
                rows={[
                  { label: 'Today', value: activeUsers.logged.day },
                  { label: 'This week', value: activeUsers.logged.week },
                  { label: 'This month', value: activeUsers.logged.month },
                ]}
                token={CHART_TOKENS[2]}
              />
            </div>
            <div>
              <h4 className="text-sm font-medium text-ink-main mb-2">Synced a device</h4>
              <RankedBars
                rows={[
                  { label: 'Today', value: activeUsers.synced.day },
                  { label: 'This week', value: activeUsers.synced.week },
                  { label: 'This month', value: activeUsers.synced.month },
                ]}
                token={CHART_TOKENS[1]}
              />
            </div>
          </div>
          <p className="text-xs text-ink-subtle mt-4 leading-snug">
            These are different populations, not two measurements of one. Someone who opens the app,
            reads their grid and logs nothing counts only in the second.
            The sync figure <strong className="text-ink-light">under-counts</strong>: it needs a device
            header that older web clients never send, and it records at most once an hour per device.
          </p>
        </Card>

        <Card title="Signed up, never started" icon={UserX} tone="warning">
          <div className="admin-nums text-2xl sm:text-3xl font-bold text-ink-main">
            {neverActivated.total.toLocaleString()}
          </div>
          <p className="text-sm text-ink-light mb-4">accounts have never logged a single habit</p>
          <RankedBars
            rows={neverActivated.buckets.map(b => ({ label: b.label, value: b.count }))}
            token={CHART_TOKENS[3]}
            emptyLabel="Everyone has logged something"
          />
          <p className="text-xs text-ink-subtle mt-4 leading-snug">
            Weight at the top is an onboarding problem. Weight at the bottom is dead accounts
            quietly dragging down every &ldquo;share of all users&rdquo; figure on the Overview tab.
          </p>
        </Card>
      </div>

      <Card title="Where Pro comes from" icon={CreditCard} tone="warning">
        <StatGrid cols={4}>
          <StatCard title="Subscriptions" value={subscriptions.subscription} subtitle="Currently in date" icon={Repeat} tone="success" />
          <StatCard title="Lifetime" value={subscriptions.lifetime} subtitle="One-off purchase" icon={CreditCard} tone="info" />
          <StatCard title="Admin granted" value={subscriptions.override} subtitle="Override, not billed" icon={UserPlus} tone="accent" />
          <StatCard title="Free" value={subscriptions.total - subscriptions.pro} icon={UserX} tone="neutral" />
        </StatGrid>
        {subscriptions.byStore.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-ink-main mb-2">By store</h4>
            <RankedBars
              rows={subscriptions.byStore.map(r => ({ label: (r.store || 'unknown').replace(/_/g, ' '), value: r.count }))}
              token={CHART_TOKENS[4]}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
